import React, { useEffect, useState, useMemo } from 'react';
import DoubleHappiness from './DoubleHappiness';
import ChineseKnot from './ChineseKnot';
import WeddingBackground from './WeddingBackground';
import LocationMap from './LocationMap';
import TableArrangement from './TableArrangement';
import GuestSeatingList from './GuestSeatingList';

const HomePage = () => {
  const [timeLeft, setTimeLeft] = useState({});
  
  // 使用useMemo优化weddingDate
  const weddingDate = useMemo(() => new Date('2025-12-27T10:00:00'), []);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const difference = weddingDate - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [weddingDate]);

  return (
    <WeddingBackground>
      {/* Hero Section */}
      <div className="relative overflow-hidden min-h-screen">
        <div className="absolute top-[20px] left-4 md:top-10 md:left-10 animate-float">
          <ChineseKnot size={60} className="md:w-20" />
        </div>
        <div className="absolute top-[20px] right-4 md:top-10 md:right-10 animate-float" style={{ animationDelay: '1s' }}>
          <ChineseKnot size={60} className="md:w-20" />
        </div>
        
        <div className="container mx-auto px-4 pt-[320px] md:pt-16 pb-16 text-center relative z-10">
          <div className="mb-8">
            <DoubleHappiness size={180} className="md:w-[120px]" />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-elegant font-bold text-chinese-red mb-4">
            我们结婚啦
          </h1>
          <p className="text-2xl md:text-3xl text-gray-700 font-chinese mb-2">
            We're Getting Married
          </p>
          
          <div className="mt-12 mb-8">
            <div className="inline-block photo-overlay-elegant rounded-2xl shadow-2xl p-8 border-2 border-chinese-red/50">
              <h2 className="text-3xl md:text-4xl font-chinese text-chinese-dark mb-4">
                武元哲 & 张羽彤
              </h2>
              <p className="text-xl md:text-2xl text-gray-600 font-elegant">
                Hans & Yutong
              </p>
            </div>
          </div>

          {/* Wedding Details */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mt-16">
            <div className="photo-overlay-soft rounded-xl p-6 shadow-2xl transform hover:scale-105 transition-transform border border-white/30">
              <div className="text-4xl mb-4">🗓️</div>
              <h3 className="text-2xl font-chinese font-bold text-chinese-red mb-2">婚礼日期</h3>
              <p className="text-lg text-gray-700">2025年12月27日</p>
              <p className="text-md text-gray-600"> December 27, 2025</p>
              <p className="text-sm text-chinese-pink mt-2">结婚 💕</p>
            </div>
            
            <div className="photo-overlay-soft rounded-xl p-6 shadow-2xl border border-white/30">
              <LocationMap 
                address="北京市朝阳区蟹岛路1号"
                englishAddress="Hall 2, Crab Island Convention Center, Chaoyang, Beijing, China"
                venueName="花嫁丽舍 (朝阳店) 爱丽丝厅"
                mapUrl="https://j.map.baidu.com/a6/i2c"
                enableAfter="2025-12-14T00:00:00"
              />
            </div>
          </div>

          {/* Countdown Timer */}
          <div className="mt-16 mb-12">
            <h3 className="text-2xl font-chinese text-chinese-dark mb-6">倒计时 Countdown</h3>
            <div className="flex justify-center gap-4 flex-wrap">
              {Object.entries(timeLeft).map(([unit, value]) => (
                <div key={unit} className="bg-chinese-red text-white rounded-lg p-4 min-w-[100px]">
                  <div className="text-3xl font-bold">{value || 0}</div>
                  <div className="text-sm capitalize">{unit}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Table Arrangement Section */}
          <div className="mt-20 max-w-6xl mx-auto">
            <h3 className="text-3xl font-chinese font-bold text-chinese-dark mb-4 text-center">
              婚礼座位 Wedding Seating
            </h3>
            <p className="text-gray-600 mb-8 text-center">婚礼现场座位安排 | Wedding venue seating arrangement</p>
            
            <div className="photo-overlay-elegant rounded-2xl p-8 shadow-2xl border border-white/30">
              <TableArrangement />
            </div>
          </div>

          {/* Guest List Section */}
          <div className="mt-20 max-w-6xl mx-auto">
            <div className="photo-overlay-elegant rounded-2xl p-8 shadow-2xl border border-white/30">
              <GuestSeatingList />
            </div>
          </div>

          {/* Schedule */}
          <div className="mt-20 max-w-3xl mx-auto">
            <h3 className="text-3xl font-chinese font-bold text-chinese-dark mb-8">
              婚礼流程 Wedding Schedule
            </h3>
            <div className="photo-overlay-elegant rounded-xl p-8 shadow-2xl border border-white/30">
              <div className="space-y-6 text-left">
                <div className="flex items-center border-b border-gray-200 pb-4">
                  <div className="text-2xl mr-4">🎊</div>
                  <div className="flex-1">
                    <div className="font-bold text-chinese-red">9:00 - 10:30</div>
                    <div>迎宾签到 Guest Arrival & Registration</div>
                  </div>
                </div>
                <div className="flex items-center border-b border-gray-200 pb-4">
                  <div className="text-2xl mr-4">💒</div>
                  <div className="flex-1">
                    <div className="font-bold text-chinese-red">9:40 - 10:08</div>
                    <div>西式婚礼仪式 Western Wedding Ceremony</div>
                  </div>
                </div>
                <div className="flex items-center border-b border-gray-200 pb-4">
                  <div className="text-2xl mr-4">💒</div>
                  <div className="flex-1">
                    <div className="font-bold text-chinese-red">10:30 - 11:58</div>
                    <div>中式婚礼主仪式 Chinese Wedding Ceremony</div>
                  </div>
                </div>
                <div className="flex items-center border-b border-gray-200 pb-4">
                  <div className="text-2xl mr-4">🥂</div>
                  <div className="flex-1">
                    <div className="font-bold text-chinese-red">12:00 - 14:00</div>
                    <div>婚宴午餐 Wedding Banquet</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Message */}
          <div className="mt-20 mb-8">
            <div className="inline-block photo-overlay-elegant rounded-xl p-6 shadow-2xl border border-white/30">
              <p className="text-lg font-chinese text-chinese-dark">
                "执子之手，与子偕老"
              </p>
              <p className="text-md font-elegant text-gray-600 mt-2">
                "Hold your hand, grow old with you"
              </p>
            </div>

            {/* 备案信息 - 图标增强 */}
            <div className="mt-4 flex justify-center">
              <div className="inline-flex flex-col sm:flex-row items-center gap-3 bg-white/5 rounded-lg px-4 py-3 border border-white/10">
                {/* 工信部备案 */}
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-400/70"></div>
                  <a
                    href="https://beian.miit.gov.cn"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-xs text-gray-700 hover:text-gray-900 transition-colors font-medium"
                  >
                    京ICP备2025151597号-1
                  </a>
                </div>

                <div className="hidden sm:block text-gray-300 text-xs">|</div>

                {/* 公安部备案 */}
                <div className="flex items-center gap-2">
                  <img
                    src="/beian.png"
                    alt="备案"
                    className="w-5 h-5 object-contain"
                    style={{ marginRight: '0.2rem' }}
                  />
                  <a
                    href="https://beian.mps.gov.cn/#/query/webSearch?code=11010102007547"
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-gray-700 hover:text-gray-900 transition-colors font-medium"
                  >
                    京公网安备11010102007547号
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </WeddingBackground>
  );
};

export default HomePage;


